import { OktaAuthOptions } from '@okta/okta-auth-js';

export interface OktaConfig extends OktaAuthOptions {
  disableAuth?: boolean;
}

export const createOktaConfig = (config: {
  issuer: string;
  clientId: string;
  redirectUri?: string;
  disableAuth?: boolean;
}): OktaConfig => {
  // When auth is disabled, provide dummy values to prevent OktaAuth initialization errors
  if (config.disableAuth) {
    return {
      issuer: 'https://dummy.okta.com/oauth2/default',
      clientId: 'dummy-client-id',
      redirectUri: config.redirectUri || `${window.location.origin}/login/callback`,
      scopes: ['openid', 'profile', 'email'],
      pkce: true,
      disableAuth: true,
    };
  }

  // Default to WPP Okta if issuer is not provided or empty
  const issuer = config.issuer || 'https://wpp.okta.com/oauth2/default';

  if (!issuer) {
    throw new Error('Okta issuer is required. Please provide a valid issuer URL.');
  }

  return {
    issuer,
    clientId: config.clientId,
    redirectUri: config.redirectUri || `${window.location.origin}/login/callback`,
    // `offline_access` requests a refresh token, and without it sessions die
    // after the ~1h access token lifetime.
    //
    // okta-auth-js has `tokenManager.autoRenew` on by default, so it does try
    // to renew — but `renewTokens()` only uses the refresh-token grant when a
    // refresh token exists. With no refresh token it falls back to
    // `getWithoutPrompt()`, a hidden cross-origin iframe to the Okta domain.
    // Chrome and Edge block the third-party cookies that flow depends on, so
    // the renewal fails silently and every subsequent request 401s with
    // "Signature has expired" — logging the user out mid-session and
    // discarding unsaved work.
    scopes: ['openid', 'profile', 'email', 'offline_access'],
    pkce: true,
    disableAuth: config.disableAuth,
  };
};
